import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { AuthScreenShell } from "../components/auth";
import { facebookService } from "../services/facebook";
import apiService from "../services/api";
import { sentry } from "../services/sentry";
import { deriveInviteViewPhase } from "./invite/deriveInviteViewPhase";
import InviteCardView from "./invite/InviteCardView";
import type { MetaConnectedAccount } from "./invite/InviteCardView";
import { getInviteDocumentTitle } from "./invite/inviteCopy";
import { notifyPrixiOpener } from "./invite/prixiInviteReturn";
import {
  isInviteDisconnectedPhase,
  isInviteSuccessPhase,
} from "./invite/invitePhaseUtils";
import type {
  InviteData,
  InviteIntegrationSource,
  InvitePlatform,
  InviteUnavailableReason,
  MetaConnectStep,
} from "./invite/inviteTypes";
import {
  buildSuggestedMappings,
  validateMappings,
  type MetaIgLinkedAsset,
  type MetaMappingRow,
} from "./invite/metaConnectUtils";
import { isOAuthReturn } from "./invite/inviteTypes";

type ApiInvitation = Awaited<
  ReturnType<typeof apiService.getInvitationByToken>
>["invitation"];

function inviteDataFromApi(inv: ApiInvitation): InviteData {
  return {
    id: inv.token,
    role: "user",
    status: (inv.status as InviteData["status"]) || "active",
    createdAt: new Date().toISOString(),
    expiresAt:
      inv.expiresAt ||
      new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: inv.companyName || inv.createdByEmail || "admin",
    companyName: inv.companyName,
    podcastTitle: inv.podcastTitle,
    podcastId: inv.podcastId,
    platform: inv.platform,
    companyImageS3Key: inv.companyImageS3Key,
    companyImageUrl: inv.companyImageUrl,
    companySlug: inv.companySlug,
    linkedAccount: inv.linkedAccount,
    integrationSource: inv.integrationSource,
    prixiPodcastId: inv.prixiPodcastId,
    companyPodcasts: inv.companyPodcasts,
    targetPodcast: inv.targetPodcast,
    metaInviteMultiPodcastEnabled: inv.metaInviteMultiPodcastEnabled,
  };
}

const InviteAcceptance: React.FC = () => {
  const { inviteToken } = useParams<{ inviteToken: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [revalidating, setRevalidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailableReason, setUnavailableReason] =
    useState<InviteUnavailableReason | null>(null);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [facebookError, setFacebookError] = useState<string | null>(null);
  const [facebookAccessToken, setFacebookAccessToken] = useState<string | null>(
    null,
  );
  const [metaPageId, setMetaPageId] = useState<string>("");
  const [metaSubmitting, setMetaSubmitting] = useState<boolean>(false);
  const [metaConnectStep, setMetaConnectStep] = useState<MetaConnectStep | null>(
    null,
  );
  const [selectedPodcastIds, setSelectedPodcastIds] = useState<string[]>([]);
  const [metaAssets, setMetaAssets] = useState<MetaIgLinkedAsset[]>([]);
  const [metaMappings, setMetaMappings] = useState<MetaMappingRow[]>([]);
  const [metaConnectedAccounts, setMetaConnectedAccounts] = useState<MetaConnectedAccount[]>([]);
  const [youtubeError, setYoutubeError] = useState<string | null>(null);
  const [tiktokError, setTiktokError] = useState<string | null>(null);
  const [spotifyError, setSpotifyError] = useState<string | null>(null);
  const [spotifyUrl, setSpotifyUrl] = useState<string>("");
  const [spotifyLoading, setSpotifyLoading] = useState<boolean>(false);
  const [inviteExpired, setInviteExpired] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [socialAccountId, setSocialAccountId] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const errorParam = useMemo(() => params.get("error"), [params]);
  const successParam = useMemo(() => params.get("success"), [params]);
  const disconnectedParam = useMemo(() => params.get("disconnected"), [params]);
  const verifiedParam = useMemo(() => params.get("verified"), [params]);

  const urlParams = useMemo(
    () => ({
      successParam,
      disconnectedParam,
      errorParam,
      verifiedParam,
    }),
    [successParam, disconnectedParam, errorParam, verifiedParam],
  );

  const SESSION_KEY = `invite-source-${inviteToken}`;

  const cachedPrixiSource = useMemo(
    () =>
      !!inviteToken &&
      sessionStorage.getItem(`invite-source-${inviteToken}`) === "prixi",
    [inviteToken],
  );

  const integrationSource = useMemo((): InviteIntegrationSource => {
    if (inviteData?.integrationSource === "prixi") return "prixi";
    if (inviteData?.integrationSource === "podscope") return "podscope";
    if (cachedPrixiSource) return "prixi";
    return "podscope";
  }, [inviteData?.integrationSource, cachedPrixiSource]);

  useEffect(() => {
    if (inviteData?.integrationSource === "prixi") {
      sessionStorage.setItem(SESSION_KEY, "prixi");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteData?.integrationSource]);

  const ytChannelId = useMemo(() => params.get("channelId"), [params]);
  const ytChannelTitle = useMemo(() => params.get("channelTitle"), [params]);
  const isYouTubeSuccess = successParam === "youtube";
  const isPlatformSuccess = !!successParam;

  const validateInvite = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = opts?.silent ?? isOAuthReturn(urlParams);

      sentry.addBreadcrumb({
        message: "Starting invite validation",
        category: "invite",
        level: "info",
        data: { inviteToken: inviteToken ? "present" : "missing", silent },
      });

      if (!inviteToken) {
        setError("Token de invitación inválido");
        setLoading(false);
        setRevalidating(false);
        return;
      }

      if (silent) {
        setRevalidating(true);
        setLoading(false);
      } else {
        setLoading(true);
      }
      setUnavailableReason(null);

      try {
        let response;
        let lastError;
        const maxRetries = 3;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            response = await apiService.getInvitationByToken(inviteToken);
            break;
          } catch (err: any) {
            lastError = err;
            const isNetworkError =
              err.message === "Network Error" || !err.response;
            if (!isNetworkError || attempt === maxRetries) throw err;
            await new Promise((resolve) =>
              setTimeout(resolve, Math.pow(2, attempt - 1) * 1000),
            );
          }
        }

        if (!response) {
          throw (
            lastError || new Error("Failed to fetch invitation after retries")
          );
        }

        const inv = response.invitation;
        if (!inv) throw new Error("Invitación no encontrada");

        const isExpired = !!(
          inv.expiresAt && new Date(inv.expiresAt) < new Date()
        );

        if (inv.status === "revoked" || isExpired) {
          setInviteData(inviteDataFromApi(inv));
          setInviteExpired(isExpired);
          setUnavailableReason(isExpired ? "expired" : "revoked");
          setError(null);
          return;
        }
        if (inv.status === "used") {
          if (!(successParam || disconnectedParam || verifiedParam)) {
            if (!inv.socialAccountId) {
              setInviteData(inviteDataFromApi(inv));
              setUnavailableReason("already_used");
              setError(null);
              return;
            }
          }
        }

        setInviteData({
          ...inviteDataFromApi(inv),
        });

        setInviteExpired(isExpired);
        setSocialAccountId(inv.socialAccountId ?? null);
        setError(null);
        setUnavailableReason(null);

        if (successParam === "youtube") {
          setYoutubeError(null);
        } else if (errorParam === "no_channel") {
          setYoutubeError(
            "La cuenta seleccionada no tiene un canal de YouTube. Por favor elige otra cuenta o crea un canal.",
          );
        } else if (errorParam === "yt_api") {
          setYoutubeError(
            "No pudimos consultar el canal de YouTube. Reintenta o verifica los permisos.",
          );
        } else if (errorParam === "oauth_error") {
          setYoutubeError("Ocurrió un error durante la autorización de Google.");
        } else if (errorParam === "tt_api") {
          setTiktokError(
            "No pudimos consultar la API de TikTok. Reintenta más tarde.",
          );
        } else if (errorParam === "tt_no_user") {
          setTiktokError("No pudimos obtener el usuario de TikTok.");
        } else if (
          errorParam === "tt_oauth_error" ||
          errorParam === "tt_oauth_denied"
        ) {
          setTiktokError("La autorización de TikTok fue cancelada o falló.");
        }
      } catch (err: any) {
        console.error("Error validating invite:", err);
        sentry.captureException(err as Error, {
          context: "invite_validation_error",
          inviteToken: inviteToken || "missing",
        });
        const isNetworkError =
          err?.message === "Network Error" || !err.response;
        const isNotFound = err?.response?.status === 404;
        setUnavailableReason(
          isNotFound ? "not_found" : isNetworkError ? "network" : "unknown",
        );
        setError(
          isNotFound
            ? "No encontramos una invitación con este enlace. Verifica que copiaste la URL completa."
            : isNetworkError
              ? "No pudimos conectar con el servidor. Verifica tu internet e intenta de nuevo."
              : "No pudimos validar esta invitación.",
        );
      } finally {
        setLoading(false);
        setRevalidating(false);
      }
    },
    [
      inviteToken,
      successParam,
      errorParam,
      disconnectedParam,
      verifiedParam,
      urlParams,
    ],
  );

  useEffect(() => {
    validateInvite({ silent: isOAuthReturn(urlParams) });
  }, [inviteToken, validateInvite, urlParams]);

  useEffect(() => {
    if (inviteData?.platform !== "meta" || !inviteData.podcastId || socialAccountId) {
      return;
    }
    setMetaConnectStep("select");
    setSelectedPodcastIds((prev) => {
      if (prev.length > 0) {
        if (inviteData.metaInviteMultiPodcastEnabled) return prev;
        return [inviteData.podcastId!];
      }
      return [inviteData.podcastId!];
    });
  }, [
    inviteData?.platform,
    inviteData?.podcastId,
    inviteData?.metaInviteMultiPodcastEnabled,
    socialAccountId,
  ]);

  const linkedAccount = useMemo(() => {
    if (!inviteData?.platform) return null;
    if (isYouTubeSuccess && ytChannelId) {
      const accountId = decodeURIComponent(ytChannelId);
      return {
        platform: "youtube" as const,
        accountId,
        username: ytChannelTitle
          ? decodeURIComponent(ytChannelTitle)
          : undefined,
        profileUrl: `https://www.youtube.com/channel/${accountId}`,
      };
    }
    if (!inviteData.linkedAccount) return null;
    return {
      platform: inviteData.platform,
      accountId: inviteData.linkedAccount.accountId,
      username: inviteData.linkedAccount.username,
      profileUrl: inviteData.linkedAccount.profileUrl,
    };
  }, [
    inviteData?.platform,
    inviteData?.linkedAccount,
    isYouTubeSuccess,
    ytChannelId,
    ytChannelTitle,
  ]);

  const companyPodcasts = useMemo(() => {
    if (inviteData?.companyPodcasts && inviteData.companyPodcasts.length > 0) {
      return inviteData.companyPodcasts;
    }
    if (inviteData?.podcastId && inviteData.podcastTitle) {
      return [
        {
          podcastId: inviteData.podcastId,
          title: inviteData.podcastTitle,
          igHandle: inviteData.targetPodcast?.igHandle,
          image: inviteData.targetPodcast?.image,
          hasMetaConnection: !!socialAccountId,
        },
      ];
    }
    return [];
  }, [inviteData, socialAccountId]);

  const viewPhase = useMemo(
    () =>
      deriveInviteViewPhase({
        loading,
        revalidating,
        error,
        unavailableReason,
        inviteExpired,
        socialAccountId,
        hasInviteData: !!inviteData,
        urlParams,
        metaNeedsPageId: false,
        metaConnectStep,
        facebookAccessToken,
        inFlight: {
          facebookLoading,
          metaSubmitting,
          spotifyLoading,
          disconnectLoading,
        },
        platformErrors: {
          facebook: facebookError,
          youtube: youtubeError,
          tiktok: tiktokError,
          spotify: spotifyError,
        },
        integrationSource,
      }),
    [
      loading,
      revalidating,
      error,
      unavailableReason,
      inviteExpired,
      socialAccountId,
      inviteData,
      urlParams,
      metaConnectStep,
      facebookAccessToken,
      facebookLoading,
      metaSubmitting,
      spotifyLoading,
      disconnectLoading,
      facebookError,
      youtubeError,
      tiktokError,
      spotifyError,
      integrationSource,
    ],
  );

  useEffect(() => {
    if (!isInviteSuccessPhase(viewPhase) && !isInviteDisconnectedPhase(viewPhase)) {
      return;
    }
    if (integrationSource !== "prixi") return;

    notifyPrixiOpener({
      platform: inviteData?.platform ?? "youtube",
      status: isInviteSuccessPhase(viewPhase) ? "success" : "disconnected",
    });

    const timer = setTimeout(() => {
      sessionStorage.removeItem(SESSION_KEY);
      window.close();
    }, 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewPhase, integrationSource, inviteData?.platform]);

  const invitePlatform: InvitePlatform = inviteData?.platform ?? "youtube";

  useEffect(() => {
    document.title = getInviteDocumentTitle(viewPhase, {
      platform: invitePlatform,
      integrationSource,
    });
  }, [viewPhase, integrationSource, invitePlatform]);

  const linkedAccountLoading =
    (isInviteSuccessPhase(viewPhase) || viewPhase === "revalidating") &&
    !linkedAccount;

  const handleDisconnect = async () => {
    if (!socialAccountId) {
      setDisconnectError("No hay cuenta conectada para desconectar");
      return;
    }
    setDisconnectLoading(true);
    setDisconnectError(null);
    try {
      const base = process.env.REACT_APP_API_URL || "http://localhost:3001";
      if (inviteData?.platform === "meta") {
        await facebookService.init();
        const user = await facebookService.login();
        const resp = await fetch(
          `${base}/api/public/invitations/${inviteToken}/disconnect/meta`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              accessToken: user.accessToken,
              pageId: metaPageId?.trim() || undefined,
            }),
          },
        );
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          throw new Error(data?.message || "No se pudo desconectar Meta");
        }
        navigate(
          `/${inviteData?.companySlug}/invitacion/${inviteToken}?disconnected=meta`,
        );
      } else if (inviteData?.platform === "youtube") {
        const resp = await fetch(
          `${base}/api/public/invitations/${inviteToken}/oauth/google/url?disconnect=1`,
        );
        const data = await resp.json();
        if (!resp.ok || !data.url)
          throw new Error(data.message || "No se pudo crear el enlace");
        window.location.href = data.url as string;
      } else if (inviteData?.platform === "tiktok") {
        const resp = await fetch(
          `${base}/api/public/invitations/${inviteToken}/oauth/tiktok/url?disconnect=1`,
        );
        const data = await resp.json();
        if (!resp.ok || !data.url)
          throw new Error(data.message || "No se pudo crear el enlace");
        window.location.href = data.url as string;
      } else if (inviteData?.platform === "spotify") {
        const resp = await fetch(
          `${base}/api/public/invitations/${inviteToken}/disconnect/spotify`,
          { method: "POST" },
        );
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          throw new Error(data?.message || "No se pudo desconectar Spotify");
        }
        navigate(
          `/${inviteData?.companySlug}/invitacion/${inviteToken}?disconnected=spotify`,
        );
      } else {
        setDisconnectError("Plataforma no soportada para desconexión");
      }
    } catch (err) {
      setDisconnectError(
        err instanceof Error ? err.message : "Error al desconectar la cuenta",
      );
    } finally {
      setDisconnectLoading(false);
    }
  };

  const handleStartYouTubeOAuth = useCallback(async () => {
    if (!inviteToken) return;
    try {
      const base = process.env.REACT_APP_API_URL || "http://localhost:3001";
      const resp = await fetch(
        `${base}/api/public/invitations/${inviteToken}/oauth/google/url`,
      );
      const data = await resp.json();
      if (!resp.ok || !data.url)
        throw new Error(data.message || "No se pudo crear el enlace");
      window.location.href = data.url as string;
    } catch (e: any) {
      setYoutubeError(e?.message || "Error al iniciar OAuth de Google");
    }
  }, [inviteToken]);

  const handleStartTikTokOAuth = useCallback(async () => {
    if (!inviteToken) return;
    try {
      const base = process.env.REACT_APP_API_URL || "http://localhost:3001";
      const resp = await fetch(
        `${base}/api/public/invitations/${inviteToken}/oauth/tiktok/url`,
      );
      const data = await resp.json();
      if (!resp.ok || !data.url)
        throw new Error(data.message || "No se pudo crear el enlace");
      window.location.href = data.url as string;
    } catch (e: any) {
      setTiktokError(e?.message || "Error al iniciar OAuth de TikTok");
    }
  }, [inviteToken]);

  const handleSpotifyConnect = useCallback(async () => {
    if (!inviteToken || !spotifyUrl.trim()) {
      setSpotifyError("Por favor ingresa una URL de Spotify válida");
      return;
    }
    setSpotifyLoading(true);
    setSpotifyError(null);
    try {
      const showIdMatch = spotifyUrl.match(/show\/([a-zA-Z0-9]+)/);
      if (!showIdMatch) {
        throw new Error(
          "URL de Spotify inválida. Debe ser del formato: https://open.spotify.com/show/...",
        );
      }
      const base = process.env.REACT_APP_API_URL || "http://localhost:3001";
      const resp = await fetch(
        `${base}/api/public/invitations/${inviteToken}/accept/spotify`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            showId: showIdMatch[1],
            showUrl: spotifyUrl.trim(),
          }),
        },
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(
          data?.message || "No se pudo conectar la cuenta de Spotify",
        );
      }
      navigate(
        `/${inviteData?.companySlug}/invitacion/${inviteToken}?success=spotify`,
      );
    } catch (err) {
      setSpotifyError(
        err instanceof Error ? err.message : "Error al conectar Spotify",
      );
    } finally {
      setSpotifyLoading(false);
    }
  }, [inviteToken, spotifyUrl, inviteData?.companySlug, navigate]);

  const apiBase = process.env.REACT_APP_API_URL || "http://localhost:3001";

  const handleMetaContinueToFacebook = async () => {
    if (!inviteToken || selectedPodcastIds.length === 0) return;
    setFacebookLoading(true);
    setFacebookError(null);
    try {
      console.log(
        "[META_ASSETS][fe] starting Meta connect",
        "selectedPodcastIds=",
        selectedPodcastIds,
      );
      await facebookService.init();
      const user = await facebookService.login();
      console.log("[META_ASSETS][fe] FB login result", {
        id: user.id,
        name: user.name,
        hasAccessToken: !!user.accessToken,
      });
      setFacebookAccessToken(user.accessToken);

      const resp = await fetch(
        `${apiBase}/api/public/invitations/${inviteToken}/meta/assets`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accessToken: user.accessToken }),
        },
      );
      const data = await resp.json().catch(() => ({}));
      console.log(
        "[META_ASSETS][fe] /meta/assets response",
        "status=",
        resp.status,
        "ok=",
        resp.ok,
        "body=",
        data,
      );
      if (!resp.ok) {
        throw new Error(
          data?.message || "No pudimos listar las cuentas de Meta otorgadas.",
        );
      }

      const assets = (data.assets ?? []) as MetaIgLinkedAsset[];
      if (assets.length === 0) {
        throw new Error(
          "No se encontraron páginas con Instagram vinculado en los permisos otorgados.",
        );
      }

      const selectedPodcasts = companyPodcasts.filter((p) =>
        selectedPodcastIds.includes(p.podcastId),
      );
      setMetaAssets(assets);
      setMetaMappings(buildSuggestedMappings(assets, selectedPodcasts));
      setMetaConnectStep("mapping");
    } catch (err) {
      setFacebookError(
        err instanceof Error
          ? err.message
          : "Error al conectar con Facebook. Por favor intenta de nuevo.",
      );
    } finally {
      setFacebookLoading(false);
    }
  };

  const handleMetaFinalize = async () => {
    if (!inviteToken || !facebookAccessToken) return;
    const validationError = validateMappings(metaMappings, selectedPodcastIds);
    if (validationError) {
      setFacebookError(validationError);
      return;
    }

    setMetaSubmitting(true);
    setFacebookError(null);
    try {
      const resp = await fetch(
        `${apiBase}/api/public/invitations/${inviteToken}/meta/finalize`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            accessToken: facebookAccessToken,
            mappings: metaMappings.map(({ pageId, igId, podcastId }) => ({
              pageId,
              igId,
              podcastId,
            })),
          }),
        },
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(
          data?.message || "No pudimos vincular las cuentas de Meta.",
        );
      }
      // Build connected accounts list for the success screen
      const connected: MetaConnectedAccount[] = metaMappings.map((m) => {
        const asset = metaAssets.find((a) => a.pageId === m.pageId);
        const podcast = companyPodcasts.find((p) => p.podcastId === m.podcastId);
        return {
          igId: m.igId,
          igUsername: asset?.igUsername,
          igProfilePictureUrl: asset?.igProfilePictureUrl,
          podcastId: m.podcastId,
          podcastTitle: podcast?.title ?? m.podcastId,
          podcastImage: podcast?.image,
        };
      });
      setMetaConnectedAccounts(connected);
      navigate(
        `/${inviteData?.companySlug}/invitacion/${inviteToken}?success=meta`,
      );
    } catch (e: unknown) {
      setFacebookError(
        e instanceof Error ? e.message : "Error al vincular cuentas de Meta.",
      );
    } finally {
      setMetaSubmitting(false);
    }
  };

  const handleMetaMappingBack = () => {
    setMetaConnectStep("select");
    setFacebookError(null);
  };

  const attemptMetaConnect = async (opts?: {
    pageId?: string;
    tokenOverride?: string;
  }) => {
    if (!inviteToken) return;
    const tokenToUse = opts?.tokenOverride || facebookAccessToken;
    if (!tokenToUse) return;
    setMetaSubmitting(true);
    setFacebookError(null);
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/public/invitations/${inviteToken}/accept/meta`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            accessToken: tokenToUse,
            pageId: opts?.pageId,
          }),
        },
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const code = (data && data.error) || "META_ERROR";
        let friendly =
          data?.message || "No pudimos conectar tu cuenta de Instagram.";
        if (code === "PAGES_PERMISSION_MISSING") {
          friendly = `Faltan permisos: ${(data?.missing || []).join(", ")}`;
        }
        throw new Error(friendly);
      }
      navigate(
        `/${inviteData?.companySlug}/invitacion/${inviteToken}?success=meta`,
      );
    } catch (e: any) {
      setFacebookError(e?.message || "Error al conectar con Meta.");
    } finally {
      setMetaSubmitting(false);
    }
  };

  const handleAcceptInvite = async () => {
    await handleMetaContinueToFacebook();
  };

  const handleSubmitMetaPageId = async () => {
    if (!inviteToken || !facebookAccessToken || !metaPageId) {
      setFacebookError(
        !metaPageId
          ? "Ingresa el Page ID de Facebook"
          : "Error interno: falta token",
      );
      return;
    }
    await attemptMetaConnect({ pageId: metaPageId.trim() });
  };

  const handleSelectedPodcastIdsChange = useCallback(
    (ids: string[]) => {
      const targetId = inviteData?.podcastId;
      if (!targetId) {
        setSelectedPodcastIds(ids);
        return;
      }
      if (!inviteData?.metaInviteMultiPodcastEnabled) {
        setSelectedPodcastIds([targetId]);
        return;
      }
      const additional = ids.filter((id) => id !== targetId);
      setSelectedPodcastIds([targetId, ...additional]);
    },
    [inviteData?.podcastId, inviteData?.metaInviteMultiPodcastEnabled],
  );

  return (
    <AuthScreenShell navbarVariant="landing">
      <InviteCardView
          viewPhase={viewPhase}
          inviteData={inviteData}
          unavailableReason={unavailableReason}
          fatalError={error}
          successParam={successParam}
          disconnectedParam={disconnectedParam}
          isPlatformSuccess={isPlatformSuccess}
          linkedAccount={linkedAccount}
          linkedAccountLoading={linkedAccountLoading}
          facebookError={facebookError}
          youtubeError={youtubeError}
          tiktokError={tiktokError}
          spotifyError={spotifyError}
          disconnectError={disconnectError}
          termsAccepted={termsAccepted}
          onTermsChange={setTermsAccepted}
          facebookLoading={facebookLoading}
          facebookAccessToken={facebookAccessToken}
          metaPageId={metaPageId}
          onMetaPageIdChange={setMetaPageId}
          metaSubmitting={metaSubmitting}
          companyPodcasts={companyPodcasts}
          targetPodcastId={inviteData?.podcastId}
          targetPodcastTitle={inviteData?.targetPodcast?.title ?? inviteData?.podcastTitle}
          selectedPodcastIds={selectedPodcastIds}
          onSelectedPodcastIdsChange={handleSelectedPodcastIdsChange}
          metaAssets={metaAssets}
          metaMappings={metaMappings}
          onMetaMappingsChange={setMetaMappings}
          onMetaContinueToFacebook={handleMetaContinueToFacebook}
          onMetaFinalize={handleMetaFinalize}
          onMetaMappingBack={handleMetaMappingBack}
          metaConnectedAccounts={metaConnectedAccounts}
          spotifyUrl={spotifyUrl}
          onSpotifyUrlChange={setSpotifyUrl}
          spotifyLoading={spotifyLoading}
          disconnectLoading={disconnectLoading}
          onMetaConnect={handleAcceptInvite}
          onMetaPageIdSubmit={handleSubmitMetaPageId}
          onYouTubeConnect={handleStartYouTubeOAuth}
          onTikTokConnect={handleStartTikTokOAuth}
          onSpotifyConnect={handleSpotifyConnect}
          onDisconnect={handleDisconnect}
          onRetry={() => window.location.reload()}
          showRetry={unavailableReason === "network"}
      />
    </AuthScreenShell>
  );
};

export default InviteAcceptance;
